import { FC, useState, useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  DatePicker,
  Select,
  InputNumber,
  Input,
  TimePicker,
  Tooltip,
  Popconfirm,
  message,
  Empty,
  Checkbox,
  Tabs,
} from "antd";
import {
  Calendar,
  Plus,
  Copy,
  Trash2,
  ChevronDown,
  ChevronRight,
  Clock,
  MapPin,
} from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import { useDetailBookingStore } from "./detail-booking.store";
import {
  useGetAvailablePositions,
  useSaveBookingExpoDays,
} from "~/services/service-ticket";
import { useQueryClient } from "@tanstack/react-query";

const { RangePicker } = DatePicker;

// Format currency helper
const formatCurrency = (amount: number): string => {
  return `$${amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

// Format time for display
const formatTimeRange = (startTime: string, duration: number): string => {
  const start = dayjs(startTime, "HH:mm");
  const end = start.add(duration, "hour");
  return `${start.format("h:mm A")} - ${end.format("h:mm A")}`;
};

// Calculate position cost with OT (anything over 8 hours)
const calculatePositionCost = (quantity: number, duration: number, hourlyRate: number): {
  regularHours: number;
  otHours: number;
  regularCost: number;
  otCost: number;
  totalCost: number;
} => {
  const regularHours = Math.min(duration, 8);
  const otHours = Math.max(0, duration - 8);
  const regularCost = quantity * regularHours * hourlyRate;
  const otCost = quantity * otHours * hourlyRate * 1.5;
  const totalCost = regularCost + otCost;
  return { regularHours, otHours, regularCost, otCost, totalCost };
};

interface ExpoDay {
  id?: string;
  serviceDate: string;
  dayLabel?: string;
  dayStartTime: string; // Event start time (e.g., "09:00")
  dayDuration: number; // Event duration in hours (e.g., 9)
  shippingAddressId?: string; // Location for this day
  notes?: string;
  positions: ExpoPosition[];
}

interface ExpoPosition {
  id?: string;
  positionId: string;
  quantity: number;
  startTime: string;
  duration: number;
  hourlyRate: number;
}

export const ExpoManagementModal: FC = () => {
  const queryClient = useQueryClient();
  const { booking, showExpoModal, setShowExpoModal } = useDetailBookingStore();

  const [days, setDays] = useState<ExpoDay[]>([]);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  // Single day add
  const [newDate, setNewDate] = useState<Dayjs | null>(null);
  const [newDayLabel, setNewDayLabel] = useState("");
  const [newDayStartTime, setNewDayStartTime] = useState<Dayjs | null>(dayjs("09:00", "HH:mm"));
  const [newDayDuration, setNewDayDuration] = useState<number>(8);

  // Date range add
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [rangeDayLabel, setRangeDayLabel] = useState("");
  const [rangeDayStartTime, setRangeDayStartTime] = useState<Dayjs | null>(dayjs("09:00", "HH:mm"));
  const [rangeDayDuration, setRangeDayDuration] = useState<number>(8);

  const [copySourceIndex, setCopySourceIndex] = useState<number | null>(null);
  const [copyTargetIndices, setCopyTargetIndices] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [addMode, setAddMode] = useState<"single" | "range">("single");

  // Fetch positions for dropdown
  const { data: positions = [] } = useGetAvailablePositions();

  // Save mutation - uses booking-level endpoint
  const saveDaysMutation = useSaveBookingExpoDays();

  // Initialize from existing expo service tickets
  useEffect(() => {
    if (showExpoModal && booking?.serviceTicket) {
      const expoTickets = booking.serviceTicket
        .filter((ticket: any) => ticket.isExpoDay)
        .sort((a: any, b: any) => new Date(a.serviceDatetime).getTime() - new Date(b.serviceDatetime).getTime());

      const convertedDays: ExpoDay[] = expoTickets.map((ticket: any) => ({
        id: ticket.id,
        serviceDate: dayjs(ticket.serviceDatetime).format("YYYY-MM-DD"),
        dayLabel: ticket.dayLabel || undefined,
        dayStartTime: ticket.dayStartTime || "09:00",
        dayDuration: Number(ticket.dayDuration) || 8,
        notes: ticket.scope || undefined,
        positions: (ticket.positions || []).map((pos: any) => ({
          id: pos.id,
          positionId: pos.positionId,
          quantity: pos.quantity,
          startTime: pos.startTime,
          duration: Number(pos.duration),
          hourlyRate: Number(pos.hourlyRate),
        })),
      }));

      setDays(convertedDays);
      if (convertedDays.length > 0) {
        setExpandedDays(new Set([0]));
      }
      setHasChanges(false);
    }
  }, [showExpoModal, booking?.serviceTicket]);

  // Calculate totals with OT
  const totals = useMemo(() => {
    let grandTotal = 0;
    const dayTotals: number[] = [];

    days.forEach((day) => {
      let dayTotal = 0;
      (day.positions || []).forEach((pos) => {
        const rate = pos.hourlyRate || positions.find((p: any) => p.id === pos.positionId)?.hourlyRate || 0;
        const { totalCost } = calculatePositionCost(pos.quantity, pos.duration, Number(rate));
        dayTotal += totalCost;
      });
      dayTotals.push(dayTotal);
      grandTotal += dayTotal;
    });

    return { grandTotal, dayTotals };
  }, [days, positions]);

  // Add a single new day
  const handleAddSingleDay = () => {
    if (!newDate) {
      message.warning("Please select a date");
      return;
    }

    const dateStr = newDate.format("YYYY-MM-DD");
    if (days.some((d) => d.serviceDate === dateStr)) {
      message.warning("This date already exists");
      return;
    }

    const newDay: ExpoDay = {
      serviceDate: dateStr,
      dayLabel: newDayLabel || `Day ${days.length + 1}`,
      dayStartTime: newDayStartTime?.format("HH:mm") || "09:00",
      dayDuration: newDayDuration || 8,
      notes: "",
      positions: [],
    };

    const newDays = [...days, newDay].sort(
      (a, b) => new Date(a.serviceDate).getTime() - new Date(b.serviceDate).getTime()
    );

    setDays(newDays);
    setExpandedDays(new Set([newDays.findIndex((d) => d.serviceDate === dateStr)]));
    setNewDate(null);
    setNewDayLabel("");
    setHasChanges(true);
  };

  // Add multiple days from date range
  const handleAddDateRange = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning("Please select a date range");
      return;
    }

    const [startDate, endDate] = dateRange;
    const newDays: ExpoDay[] = [];
    let currentDate = startDate;
    let dayNumber = days.length + 1;

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, "day")) {
      const dateStr = currentDate.format("YYYY-MM-DD");

      // Skip if date already exists
      if (!days.some((d) => d.serviceDate === dateStr)) {
        newDays.push({
          serviceDate: dateStr,
          dayLabel: rangeDayLabel ? `${rangeDayLabel} - Day ${dayNumber}` : `Day ${dayNumber}`,
          dayStartTime: rangeDayStartTime?.format("HH:mm") || "09:00",
          dayDuration: rangeDayDuration || 8,
          notes: "",
          positions: [],
        });
        dayNumber++;
      }

      currentDate = currentDate.add(1, "day");
    }

    if (newDays.length === 0) {
      message.warning("All dates in this range already exist");
      return;
    }

    const allDays = [...days, ...newDays].sort(
      (a, b) => new Date(a.serviceDate).getTime() - new Date(b.serviceDate).getTime()
    );

    setDays(allDays);
    // Expand the first new day
    const firstNewDayIndex = allDays.findIndex((d) => d.serviceDate === newDays[0].serviceDate);
    setExpandedDays(new Set([firstNewDayIndex]));

    setDateRange(null);
    setRangeDayLabel("");
    setHasChanges(true);
    message.success(`Added ${newDays.length} day(s)`);
  };

  // Add position to a day
  const addPosition = (dayIndex: number) => {
    const newDays = [...days];
    const day = newDays[dayIndex];
    if (!day.positions) {
      day.positions = [];
    }
    // Default position start time to day start time
    day.positions.push({
      positionId: "",
      quantity: 1,
      startTime: day.dayStartTime || "09:00",
      duration: day.dayDuration || 8,
      hourlyRate: 0,
    });
    setDays(newDays);
    setHasChanges(true);
  };

  // Update position
  const updatePosition = (
    dayIndex: number,
    posIndex: number,
    field: keyof ExpoPosition,
    value: any
  ) => {
    const newDays = [...days];
    const position = newDays[dayIndex].positions[posIndex];

    if (field === "positionId") {
      const selectedPosition = positions.find((p: any) => p.id === value);
      position.positionId = value;
      position.hourlyRate = selectedPosition?.hourlyRate || 0;
    } else {
      (position as any)[field] = value;
    }

    setDays(newDays);
    setHasChanges(true);
  };

  // Remove position
  const removePosition = (dayIndex: number, posIndex: number) => {
    const newDays = [...days];
    newDays[dayIndex].positions.splice(posIndex, 1);
    setDays(newDays);
    setHasChanges(true);
  };

  // Update day field
  const updateDay = (dayIndex: number, field: keyof ExpoDay, value: any) => {
    const newDays = [...days];
    (newDays[dayIndex] as any)[field] = value;
    setDays(newDays);
    setHasChanges(true);
  };

  // Delete a day
  const deleteDay = (dayIndex: number) => {
    const newDays = [...days];
    newDays.splice(dayIndex, 1);
    setDays(newDays);
    setHasChanges(true);
  };

  // Toggle day expansion
  const toggleDayExpanded = (dayIndex: number) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(dayIndex)) {
      newExpanded.delete(dayIndex);
    } else {
      newExpanded.add(dayIndex);
    }
    setExpandedDays(newExpanded);
  };

  // Start copy mode
  const startCopyMode = (sourceIndex: number) => {
    const sourceDay = days[sourceIndex];
    if (sourceDay.positions.length === 0) {
      message.warning("Source day has no positions to copy");
      return;
    }
    setCopySourceIndex(sourceIndex);
    setCopyTargetIndices(new Set());
  };

  // Cancel copy mode
  const cancelCopyMode = () => {
    setCopySourceIndex(null);
    setCopyTargetIndices(new Set());
  };

  // Toggle copy target
  const toggleCopyTarget = (index: number) => {
    const newTargets = new Set(copyTargetIndices);
    if (newTargets.has(index)) {
      newTargets.delete(index);
    } else {
      newTargets.add(index);
    }
    setCopyTargetIndices(newTargets);
  };

  // Execute copy (copies positions AND day timing)
  const executeCopy = () => {
    if (copySourceIndex === null || copyTargetIndices.size === 0) {
      message.warning("Please select target days");
      return;
    }

    const sourceDay = days[copySourceIndex];
    const newDays = days.map((day, index) => {
      if (copyTargetIndices.has(index)) {
        return {
          ...day,
          dayStartTime: sourceDay.dayStartTime,
          dayDuration: sourceDay.dayDuration,
          shippingAddressId: sourceDay.shippingAddressId, // Copy location too
          positions: sourceDay.positions.map((pos) => ({
            ...pos,
            id: undefined, // Remove ID to create new positions
          })),
        };
      }
      return day;
    });

    setDays(newDays);
    setHasChanges(true);
    message.success(`Copied to ${copyTargetIndices.size} day(s)`);
    cancelCopyMode();
  };

  // Save all changes
  const handleSave = async () => {
    if (!booking?.id) return;

    // Validate
    for (const day of days) {
      if (day.positions.length === 0) {
        message.warning("Each day must have at least one position");
        return;
      }
      for (const pos of day.positions) {
        if (!pos.positionId) {
          message.error("Please select a position for all entries");
          return;
        }
      }
    }

    setIsSaving(true);

    // Clean data for API
    const cleanedDays = days.map((day) => ({
      ...(day.id ? { id: day.id } : {}),
      serviceDate: day.serviceDate,
      dayLabel: day.dayLabel || "",
      dayStartTime: day.dayStartTime || "09:00",
      dayDuration: Number(day.dayDuration) || 8,
      shippingAddressId: day.shippingAddressId || null,
      notes: day.notes || "",
      positions: day.positions.map((pos) => {
        const rate = pos.hourlyRate ?? positions.find((p: any) => p.id === pos.positionId)?.hourlyRate ?? 0;
        return {
          ...(pos.id ? { id: pos.id } : {}),
          positionId: pos.positionId,
          quantity: Number(pos.quantity) || 1,
          startTime: pos.startTime || "08:00",
          duration: Number(pos.duration) || 8,
          hourlyRate: Number(rate) || 0,
        };
      }),
    }));

    try {
      await saveDaysMutation.mutateAsync({
        bookingId: booking.id,
        days: cleanedDays,
      });

      // Invalidate and refetch booking data
      await queryClient.invalidateQueries({ queryKey: ["booking", booking.id] });

      message.success("Expo days saved successfully");
      setHasChanges(false);
      setShowExpoModal(false);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to save expo days");
    } finally {
      setIsSaving(false);
    }
  };

  const resetAndClose = () => {
    setDays([]);
    setExpandedDays(new Set());
    setHasChanges(false);
    setCopySourceIndex(null);
    setCopyTargetIndices(new Set());
    setNewDate(null);
    setNewDayLabel("");
    setDateRange(null);
    setRangeDayLabel("");
    setShowExpoModal(false);
  };

  const handleClose = () => {
    if (hasChanges) {
      Modal.confirm({
        title: "Unsaved Changes",
        content: "You have unsaved changes. Are you sure you want to close?",
        onOk: resetAndClose,
      });
    } else {
      resetAndClose();
    }
  };

  return (
    <Modal
      open={showExpoModal}
      onCancel={handleClose}
      width={1100}
      title={
        <div className="flex items-center space-x-2">
          <Calendar className="text-purple-500" size={20} />
          <span>Expo Mode - {booking?.project?.name || "Project"}</span>
        </div>
      }
      footer={
        <div className="flex justify-between items-center">
          <div className="text-lg font-semibold text-purple-900">
            Total: {formatCurrency(totals.grandTotal)}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSave}
              loading={isSaving}
              disabled={!hasChanges || days.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Save Expo Days
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {/* Add Days Section */}
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <Tabs
            activeKey={addMode}
            onChange={(key) => setAddMode(key as "single" | "range")}
            items={[
              {
                key: "single",
                label: "Add Single Day",
                children: (
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center gap-3">
                      <DatePicker
                        value={newDate}
                        onChange={setNewDate}
                        placeholder="Select date"
                        className="w-40"
                        disabledDate={(current) => {
                          return days.some((d) => d.serviceDate === current.format("YYYY-MM-DD"));
                        }}
                      />
                      <Input
                        value={newDayLabel}
                        onChange={(e) => setNewDayLabel(e.target.value)}
                        placeholder="Label (e.g., Load Out, Setup)"
                        className="w-48"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-gray-500" />
                        <span className="text-sm text-gray-600">Event Time:</span>
                      </div>
                      <TimePicker
                        value={newDayStartTime}
                        onChange={setNewDayStartTime}
                        format="h:mm A"
                        className="w-28"
                        minuteStep={15}
                        placeholder="Start"
                      />
                      <InputNumber
                        value={newDayDuration}
                        onChange={(val) => setNewDayDuration(val || 8)}
                        min={1}
                        max={24}
                        className="w-24"
                        addonAfter="hrs"
                      />
                      <Button
                        type="primary"
                        icon={<Plus size={14} />}
                        onClick={handleAddSingleDay}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Add Day
                      </Button>
                    </div>
                  </div>
                ),
              },
              {
                key: "range",
                label: "Add Date Range",
                children: (
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center gap-3">
                      <RangePicker
                        value={dateRange}
                        onChange={(dates) => setDateRange(dates)}
                        className="w-64"
                        disabledDate={(current) => {
                          return days.some((d) => d.serviceDate === current.format("YYYY-MM-DD"));
                        }}
                      />
                      <Input
                        value={rangeDayLabel}
                        onChange={(e) => setRangeDayLabel(e.target.value)}
                        placeholder="Label prefix (e.g., Art Basel)"
                        className="w-48"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-gray-500" />
                        <span className="text-sm text-gray-600">Default Event Time:</span>
                      </div>
                      <TimePicker
                        value={rangeDayStartTime}
                        onChange={setRangeDayStartTime}
                        format="h:mm A"
                        className="w-28"
                        minuteStep={15}
                        placeholder="Start"
                      />
                      <InputNumber
                        value={rangeDayDuration}
                        onChange={(val) => setRangeDayDuration(val || 8)}
                        min={1}
                        max={24}
                        className="w-24"
                        addonAfter="hrs"
                      />
                      <Button
                        type="primary"
                        icon={<Plus size={14} />}
                        onClick={handleAddDateRange}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Add Days
                      </Button>
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>

        {/* Copy Mode Banner */}
        {copySourceIndex !== null && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 flex items-center justify-between">
            <div>
              <span className="font-medium">Copy Mode:</span> Select days to copy timing & positions from{" "}
              <strong>{days[copySourceIndex].dayLabel || dayjs(days[copySourceIndex].serviceDate).format("MMM D")}</strong>
            </div>
            <div className="flex gap-2">
              <Button onClick={cancelCopyMode}>Cancel</Button>
              <Button
                type="primary"
                onClick={executeCopy}
                disabled={copyTargetIndices.size === 0}
              >
                Copy to {copyTargetIndices.size} Day(s)
              </Button>
            </div>
          </div>
        )}

        {/* Days List */}
        {days.length === 0 ? (
          <Empty
            description="No expo days yet. Add a day or date range to get started."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className="space-y-3">
            {days.map((day, dayIndex) => {
              const isExpanded = expandedDays.has(dayIndex);
              const dayTotal = totals.dayTotals[dayIndex] || 0;
              const isSource = copySourceIndex === dayIndex;
              const isTarget = copySourceIndex !== null && copySourceIndex !== dayIndex;

              return (
                <div
                  key={dayIndex}
                  className={`border rounded-lg ${
                    isSource ? "border-blue-500 bg-blue-50" :
                    isTarget && copyTargetIndices.has(dayIndex) ? "border-green-500 bg-green-50" :
                    "border-gray-200"
                  }`}
                >
                  {/* Day Header */}
                  <div
                    className={`flex items-center justify-between p-3 ${
                      isExpanded ? "border-b" : ""
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {isTarget && (
                        <Checkbox
                          checked={copyTargetIndices.has(dayIndex)}
                          onChange={() => toggleCopyTarget(dayIndex)}
                        />
                      )}
                      <button
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => toggleDayExpanded(dayIndex)}
                      >
                        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </button>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold">
                            {dayjs(day.serviceDate).format("ddd, MMM D, YYYY")}
                          </span>
                          <Input
                            value={day.dayLabel}
                            onChange={(e) => updateDay(dayIndex, "dayLabel", e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Day label"
                            className="w-36 text-sm"
                            size="small"
                          />
                          {isSource && (
                            <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
                              SOURCE
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-purple-600 font-medium">
                            Event: {formatTimeRange(day.dayStartTime, day.dayDuration)}
                          </span>
                          <span className="text-xs text-gray-400">|</span>
                          <span className="text-sm text-gray-500">
                            {day.positions.length} position{day.positions.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Day Total</div>
                        <div className="font-semibold text-purple-600">
                          {formatCurrency(dayTotal)}
                        </div>
                      </div>
                      {copySourceIndex === null && (
                        <div className="flex space-x-1">
                          <Tooltip title="Copy to other days">
                            <Button
                              size="small"
                              icon={<Copy size={14} />}
                              onClick={() => startCopyMode(dayIndex)}
                              disabled={day.positions.length === 0}
                            />
                          </Tooltip>
                          <Popconfirm
                            title="Delete this day?"
                            description="This will delete the day and all its positions"
                            onConfirm={() => deleteDay(dayIndex)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button size="small" danger icon={<Trash2 size={14} />} />
                          </Popconfirm>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Day Content - Event Timing & Positions */}
                  {isExpanded && (
                    <div className="p-3">
                      {/* Day Event Timing & Location */}
                      <div className="bg-purple-50 p-3 rounded-lg mb-3 space-y-3">
                        <div className="flex items-center gap-4 flex-wrap">
                          <div className="flex items-center gap-2">
                            <Clock size={14} className="text-purple-600" />
                            <span className="text-sm font-medium text-purple-800">Event Time:</span>
                          </div>
                          <TimePicker
                            value={day.dayStartTime ? dayjs(day.dayStartTime, "HH:mm") : undefined}
                            onChange={(time) =>
                              updateDay(dayIndex, "dayStartTime", time?.format("HH:mm") || "09:00")
                            }
                            format="h:mm A"
                            className="w-28"
                            size="small"
                            minuteStep={15}
                          />
                          <span className="text-gray-400">to</span>
                          <InputNumber
                            value={day.dayDuration}
                            onChange={(val) => updateDay(dayIndex, "dayDuration", val || 8)}
                            min={1}
                            max={24}
                            className="w-24"
                            size="small"
                            addonAfter="hrs"
                          />
                          <span className="text-sm text-gray-500">
                            (ends {dayjs(day.dayStartTime, "HH:mm").add(day.dayDuration, "hour").format("h:mm A")})
                          </span>
                        </div>

                        {/* Location Selection */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <MapPin size={14} className="text-purple-600" />
                            <span className="text-sm font-medium text-purple-800">Location:</span>
                          </div>
                          <Select
                            value={day.shippingAddressId || undefined}
                            onChange={(value) => updateDay(dayIndex, "shippingAddressId", value)}
                            placeholder="Select location"
                            className="flex-1 min-w-[250px]"
                            size="small"
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={(booking?.client?.shippingAddresses || []).map((addr: any) => ({
                              value: addr.id,
                              label: `${addr.name || addr.city || "Address"} - ${addr.address || ""} ${addr.city || ""}, ${addr.state || ""}`.trim(),
                            }))}
                          />
                          {copySourceIndex === null && day.shippingAddressId && (
                            <Tooltip title="Copy location to other days">
                              <Button
                                size="small"
                                type="link"
                                onClick={() => {
                                  // Quick copy location to all other days
                                  const newDays = days.map((d, i) =>
                                    i === dayIndex ? d : { ...d, shippingAddressId: day.shippingAddressId }
                                  );
                                  setDays(newDays);
                                  setHasChanges(true);
                                  message.success("Location copied to all days");
                                }}
                              >
                                Copy to all
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      {/* Notes */}
                      <div className="mb-3">
                        <Input.TextArea
                          value={day.notes}
                          onChange={(e) => updateDay(dayIndex, "notes", e.target.value)}
                          placeholder="Day notes (optional)"
                          rows={2}
                          className="text-sm"
                        />
                      </div>

                      {/* Positions Table */}
                      {day.positions.length > 0 && (
                        <div className="space-y-2 mb-3">
                          <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-2">
                            <div className="col-span-4">Position</div>
                            <div className="col-span-1">Qty</div>
                            <div className="col-span-2">Start Time</div>
                            <div className="col-span-2">Duration</div>
                            <div className="col-span-2">Subtotal</div>
                            <div className="col-span-1"></div>
                          </div>

                          {day.positions.map((pos, posIndex) => {
                            const rate = pos.hourlyRate || positions.find((p: any) => p.id === pos.positionId)?.hourlyRate || 0;
                            const { regularHours, otHours, regularCost, otCost, totalCost } = calculatePositionCost(
                              pos.quantity,
                              pos.duration,
                              Number(rate)
                            );
                            const hasOT = otHours > 0;

                            return (
                              <div
                                key={posIndex}
                                className={`grid grid-cols-12 gap-2 items-center p-2 rounded ${
                                  hasOT ? "bg-orange-50 border border-orange-200" : "bg-gray-50"
                                }`}
                              >
                                <div className="col-span-4">
                                  <Select
                                    value={pos.positionId || undefined}
                                    onChange={(value) => updatePosition(dayIndex, posIndex, "positionId", value)}
                                    placeholder="Select position"
                                    className="w-full"
                                    size="small"
                                    showSearch
                                    filterOption={(input, option) =>
                                      (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={positions.map((p: any) => ({
                                      value: p.id,
                                      label: `${p.name} ($${p.hourlyRate}/hr)`,
                                    }))}
                                  />
                                </div>
                                <div className="col-span-1">
                                  <InputNumber
                                    value={pos.quantity}
                                    onChange={(value) => updatePosition(dayIndex, posIndex, "quantity", value || 1)}
                                    min={1}
                                    className="w-full"
                                    size="small"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <TimePicker
                                    value={pos.startTime ? dayjs(pos.startTime, "HH:mm") : undefined}
                                    onChange={(time) =>
                                      updatePosition(dayIndex, posIndex, "startTime", time?.format("HH:mm") || "08:00")
                                    }
                                    format="h:mm A"
                                    className="w-full"
                                    size="small"
                                    minuteStep={15}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <InputNumber
                                    value={pos.duration}
                                    onChange={(value) => updatePosition(dayIndex, posIndex, "duration", value || 1)}
                                    min={0.5}
                                    step={0.5}
                                    className="w-full"
                                    size="small"
                                    addonAfter="hrs"
                                  />
                                </div>
                                <div className="col-span-2 text-right">
                                  {hasOT ? (
                                    <Tooltip
                                      title={
                                        <div className="text-xs">
                                          <div>{regularHours}h × ${Number(rate).toFixed(2)} = {formatCurrency(regularCost)}</div>
                                          <div className="text-orange-300">
                                            {otHours}h OT × ${(Number(rate) * 1.5).toFixed(2)} = {formatCurrency(otCost)}
                                          </div>
                                        </div>
                                      }
                                    >
                                      <div>
                                        <div className="font-medium text-sm">{formatCurrency(totalCost)}</div>
                                        <div className="text-xs text-orange-600 font-medium">+{otHours}h OT</div>
                                      </div>
                                    </Tooltip>
                                  ) : (
                                    <div className="font-medium text-sm">{formatCurrency(totalCost)}</div>
                                  )}
                                </div>
                                <div className="col-span-1 text-right">
                                  <Button
                                    size="small"
                                    type="text"
                                    danger
                                    icon={<Trash2 size={14} />}
                                    onClick={() => removePosition(dayIndex, posIndex)}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <Button
                        type="dashed"
                        icon={<Plus size={14} />}
                        onClick={() => addPosition(dayIndex)}
                        className="w-full"
                      >
                        Add Position
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};
